import React, { useEffect, useState, useCallback } from 'react';
import { useScenario } from '../context/ScenarioContext';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { KpiCard } from '../components/KpiCard';
import { PageHeader, LoadingKpis, ErrorState } from '../components/States';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { formatCurrency, formatPercent, formatNumber, formatEurPerWp, formatEurPerDay } from '../lib/format';
import { PieChart, Pie, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';
import { Euro, TrendingUp, Activity, Wallet, Banknote, Gauge, Building2, Target, Boxes, Factory, Percent, Globe, Pencil, Check, X, Receipt, Download, Loader2, Scale } from 'lucide-react';
import { toast } from 'sonner';

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

const CHART = ['hsl(205,78%,45%)', 'hsl(38,92%,50%)', 'hsl(160,60%,42%)', 'hsl(262,52%,55%)', 'hsl(215,14%,52%)', 'hsl(0,72%,55%)', 'hsl(215,10%,68%)'];
const DIV_LABELS = { Installatori: 'Installatori', BtoB: 'BtoB', Impianti: 'Impianti', ELP: 'ELP', Altro: 'Altro' };

function ChartCard({ title, subtitle, children }) {
  return (
    <Card className="p-5">
      <div className="mb-3"><h3 className="text-sm font-semibold text-foreground">{title}</h3>{subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}</div>
      <div data-testid="recharts-chart">{children}</div>
    </Card>
  );
}

function EuroTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  return (
    <div className="rounded-lg border bg-card px-3 py-2 shadow-sm" data-testid="recharts-tooltip">
      <div className="text-sm font-medium tabular-nums" style={{ color: p.payload?.fill || p.color }}>{p.name}: {formatCurrency(p.value)}</div>
    </div>
  );
}

function DonutChart({ data, testId }) {
  const total = data.reduce((s, d) => s + (d.value || 0), 0) || 1;
  return (
    <div>
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={82} paddingAngle={2} data-testid={testId}>
            {data.map((e, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
          </Pie>
          <Tooltip content={<EuroTooltip />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
        {data.map((e, i) => (
          <div key={i} className="flex items-center justify-between gap-2 text-xs" data-testid={`legend-item-${i}`}>
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: CHART[i % CHART.length] }} />
              <span className="text-muted-foreground truncate">{e.name}</span>
            </span>
            <span className="shrink-0 tabular-nums whitespace-nowrap">
              <span className="font-semibold text-foreground">{formatCurrency(e.value)}</span>
              <span className="text-muted-foreground ml-1">({formatPercent((e.value || 0) / total)})</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Riga statistica per le card riepilogative
function Stat({ label, value, hint, strong, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5" data-testid={testId}>
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`tabular-nums ${strong ? 'text-base font-semibold text-foreground' : 'text-sm font-medium text-foreground'}`}>{value}{hint && <span className="ml-1 text-[11px] font-normal text-muted-foreground">{hint}</span>}</span>
    </div>
  );
}

function DivisionMarginCard({ title, icon: Icon, revenue, dailySales, marginBase, coverage, marginEff, marginEur }) {
  return (
    <Card className="p-5" data-testid={`margin-card-${title}`}>
      <div className="flex items-center gap-2 mb-3"><span className="h-8 w-8 rounded-lg bg-primary/10 text-primary grid place-items-center"><Icon className="h-4 w-4" /></span><h4 className="text-sm font-semibold text-foreground">{title}</h4></div>
      <Stat label="Ricavi divisione" value={formatCurrency(revenue)} />
      <Stat label="Vendita giornaliera" value={formatEurPerDay(dailySales)} testId={`margin-daily-${title}`} />
      <Stat label="Margine ponderato (base)" value={formatPercent(marginBase)} />
      <Stat label="Copertura finanziaria" value={coverage > 0 ? `+${formatPercent(coverage)}` : formatPercent(coverage)} />
      <div className="my-1 border-t" />
      <Stat label="Margine medio effettivo" value={formatPercent(marginEff)} strong />
      <Stat label="Margine €/anno" value={formatCurrency(marginEur)} strong />
    </Card>
  );
}

// Riga di conto economico (con eventuale editing inline)
function PLRow({ label, amount, kind, editable, isEditing, draft, setDraft, onEdit, onSave, onCancel, saving, testId }) {
  // kind: 'add' | 'sub' | 'total' | 'muted'
  const signChar = kind === 'add' ? '+' : kind === 'sub' ? '−' : '';
  const isTotal = kind === 'total' || kind === 'muted';
  const colorCls = kind === 'add' ? 'delta-good' : kind === 'sub' ? 'delta-bad' : kind === 'muted' ? 'text-foreground' : (amount >= 0 ? 'delta-good' : 'delta-bad');
  return (
    <div className="flex items-center justify-between gap-3 py-2" data-testid={testId}>
      <div className="flex items-center gap-1.5 min-w-0">
        {signChar && <span className="text-muted-foreground text-sm shrink-0">{signChar}</span>}
        <span className={`text-sm truncate ${isTotal ? 'font-semibold text-foreground' : 'text-foreground'}`}>{label}</span>
        {editable && !isEditing && (
          <button onClick={onEdit} className="shrink-0 text-muted-foreground hover:text-primary transition-colors" data-testid={`${testId}-edit`} aria-label={`Modifica ${label}`}><Pencil className="h-3.5 w-3.5" /></button>
        )}
      </div>
      {isEditing ? (
        <div className="flex items-center gap-1 shrink-0">
          <Input type="number" step="0.01" value={draft} onChange={(e) => setDraft(e.target.value)} className="h-8 w-36 text-right tabular-nums" autoFocus data-testid={`${testId}-input`} />
          <Button size="icon" variant="ghost" className="h-8 w-8 text-[hsl(var(--success))]" onClick={onSave} disabled={saving} data-testid={`${testId}-save`}><Check className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" onClick={onCancel} disabled={saving} data-testid={`${testId}-cancel`}><X className="h-4 w-4" /></Button>
        </div>
      ) : (
        <span className={`tabular-nums shrink-0 ${colorCls} ${isTotal ? 'text-base font-semibold' : 'text-sm font-medium'}`}>
          {signChar === '+' ? '+ ' : signChar === '−' ? '− ' : ''}{formatCurrency(Math.abs(amount))}
        </span>
      )}
    </div>
  );
}

// Dettaglio esploso di una somma (costi fissi / variabili) — perimetro grigio chiaro
function CostBreakdown({ items, testId }) {
  const rows = (items || []).filter((i) => Math.abs(i.value || 0) > 0.5).sort((a, b) => (b.value || 0) - (a.value || 0));
  if (!rows.length) return null;
  return (
    <div className="ml-4 mb-1.5 rounded-lg border border-border/70 bg-muted/40 px-3 py-2 space-y-1" data-testid={testId}>
      {rows.map((r, i) => (
        <div key={i} className="flex items-center justify-between gap-3 text-xs" data-testid={`${testId}-row`}>
          <span className="text-muted-foreground truncate">{r.label}</span>
          <span className="tabular-nums text-muted-foreground font-medium shrink-0">{formatCurrency(r.value)}</span>
        </div>
      ))}
    </div>
  );
}

function ContoEconomico({ kpi, assumptions, canEdit, sid, onSaved }) {
  const [editing, setEditing] = useState(null); // chiave campo in modifica
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const startEdit = (key, current) => { setEditing(key); setDraft(String(current ?? 0)); };
  const cancel = () => { setEditing(null); setDraft(''); };
  const save = async (key) => {
    const num = parseFloat(String(draft).replace(',', '.')) || 0;
    setSaving(true);
    try {
      const patch = { ...(assumptions || {}) };
      if (key === 'contributo_fier') patch.contributo_fier = num;
      if (key === 'financial_charges') { patch.financial_charges = num; patch.financial_charges_pct_revenue = 0; }
      if (key === 'premio_direzione') patch.premio_direzione = num;
      await api.updateScenario(sid, { assumptions: patch });
      toast.success('Conto economico aggiornato');
      setEditing(null);
      onSaved && onSaved();
    } catch (e) {
      toast.error('Errore nel salvataggio');
    } finally { setSaving(false); }
  };

  const rowProps = (key) => ({
    isEditing: editing === key,
    draft, setDraft, saving,
    onEdit: () => startEdit(key, key === 'financial_charges' ? kpi.financial_charges_annual : kpi[key]),
    onSave: () => save(key),
    onCancel: cancel,
  });

  // Utile Netto = Utile d'esercizio × 0,7 (30% tasse); in perdita resta invariato
  const utileNetto = kpi.utile_esercizio > 0 ? kpi.utile_esercizio * 0.7 : kpi.utile_esercizio;

  // Dettaglio esploso dei Costi Fissi (somma = kpi.fixed_costs_annual)
  const fixedGenerali = (kpi.fixed_costs_annual || 0) - (kpi.employees_total_annual || 0) - (kpi.agents_fixed_annual || 0) - (kpi.marketing_annual || 0) - (kpi.sponsor_annual || 0) - (kpi.depreciation_annual || 0);
  const fixedBreakdown = [
    { label: 'Personale', value: kpi.employees_total_annual || 0 },
    { label: 'Rete Vendita (fissa)', value: kpi.agents_fixed_annual || 0 },
    { label: 'Costi fissi generali', value: fixedGenerali },
    { label: 'Marketing', value: kpi.marketing_annual || 0 },
    { label: 'Sponsor', value: kpi.sponsor_annual || 0 },
    { label: 'Ammortamenti', value: kpi.depreciation_annual || 0 },
  ];
  // Dettaglio esploso dei Costi Variabili (somma = kpi.variable_costs_annual)
  const variableBreakdown = [
    { label: 'Provvigioni agenti (Rete Vendita)', value: kpi.agents_variable_annual || 0 },
    { label: 'Altri costi variabili', value: (kpi.variable_costs_annual || 0) - (kpi.agents_variable_annual || 0) },
  ];

  return (
    <Card className="p-5" data-testid="conto-economico">
      <div className="flex items-center gap-2 mb-4"><span className="h-8 w-8 rounded-lg bg-primary/10 text-primary grid place-items-center"><Receipt className="h-4 w-4" /></span><h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Conto Economico</h3></div>

      <PLRow label="Ricavi Totali" amount={kpi.revenues_total} kind="muted" testId="ce-ricavi" />
      <div className="flex items-center justify-between gap-3 pt-1">
        <span className="text-sm text-foreground">Margine Operativo</span>
        <span className="tabular-nums text-base font-semibold delta-good" data-testid="ce-margine-operativo">{formatCurrency(kpi.margine_operativo)}</span>
      </div>
      <p className="text-xs text-muted-foreground -mt-0.5 mb-1">({formatPercent(kpi.margine_operativo_pct)} su ricavi)</p>

      <div className="border-t my-2" />

      <PLRow label="Costi Fissi Totali (incl. Rete Vendita fissa)" amount={kpi.fixed_costs_annual} kind="sub" testId="ce-costi-fissi" />
      <CostBreakdown items={fixedBreakdown} testId="ce-costi-fissi-breakdown" />
      <PLRow label="Contributo FIER Energia" amount={kpi.contributo_fier} kind="add" editable={canEdit} testId="ce-contributo" {...rowProps('contributo_fier')} />
      <PLRow label="Costi Variabili (provvigioni agenti)" amount={kpi.variable_costs_annual} kind="sub" testId="ce-costi-variabili" />
      <CostBreakdown items={variableBreakdown} testId="ce-costi-variabili-breakdown" />
      <PLRow label="Gap EU/Asia (beneficio)" amount={kpi.gap_eu_asia} kind="add" testId="ce-gap" />
      <PLRow label="Oneri Finanziari" amount={kpi.financial_charges_annual} kind="sub" editable={canEdit} testId="ce-oneri" {...rowProps('financial_charges')} />
      <PLRow label="Premio Direzione" amount={kpi.premio_direzione} kind="sub" editable={canEdit} testId="ce-premio" {...rowProps('premio_direzione')} />

      <div className="border-t my-2" />

      <PLRow label="Utile d'esercizio" amount={kpi.utile_esercizio} kind="total" testId="ce-utile-esercizio" />
      <PLRow label="Utile Netto (post tasse)" amount={utileNetto} kind="total" testId="ce-utile-netto" />

      <div className="border-t my-2" />

      <div className="flex items-center justify-between gap-3 py-1.5">
        <span className="text-sm font-semibold text-foreground">EBITDA</span>
        <span className="tabular-nums text-base font-semibold text-foreground" data-testid="ce-ebitda">{formatCurrency(kpi.ebitda)}</span>
      </div>
      <p className="text-xs text-muted-foreground -mt-1">({formatPercent(kpi.ebitda_pct)} su ricavi · include ammortamenti)</p>
      <div className="flex items-center justify-between gap-3 py-1.5">
        <span className="text-sm font-semibold text-foreground">EBIT</span>
        <span className="tabular-nums text-base font-semibold text-foreground" data-testid="ce-ebit">{formatCurrency(kpi.ebit)}</span>
      </div>
      <p className="text-xs text-muted-foreground -mt-1">({formatPercent(kpi.ebit_pct)} su ricavi · risultato operativo)</p>

      {!canEdit && <p className="mt-3 text-[11px] text-muted-foreground">Le voci Oneri Finanziari, Contributo FIER Energia e Premio Direzione sono modificabili dalla Direzione.</p>}
    </Card>
  );
}

function CashFlowDialog({ open, onOpenChange, kpi, assumptions, sid, onSaved }) {
  const initial = () => {
    const arr = Array.isArray(kpi.rate_mutui_monthly) && kpi.rate_mutui_monthly.length === 12
      ? kpi.rate_mutui_monthly
      : Array(12).fill((kpi.rate_mutui || 0) / 12);
    return arr.map((v) => String(Math.round(v || 0)));
  };
  const [rates, setRates] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setRates(initial()); /* eslint-disable-next-line */ }, [open]);

  const nums = rates.map((r) => parseFloat(String(r).replace(',', '.')) || 0);
  const totale = nums.reduce((s, v) => s + v, 0);
  const cashFlow = (kpi.ebitda || 0) - totale;

  const save = async () => {
    setSaving(true);
    try {
      await api.updateScenario(sid, { assumptions: { ...(assumptions || {}), rate_mutui_monthly: nums, rate_mutui: totale } });
      toast.success('Rate mensili aggiornate', { description: `Cash Flow ${formatCurrency(cashFlow)}` });
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error('Errore nel salvataggio delle rate'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="cashflow-dialog">
        <DialogHeader>
          <DialogTitle>Rate mutui, finanziamenti e piani di rientro</DialogTitle>
          <DialogDescription>Cash Flow (semplificato) = EBITDA − somma delle rate mensili</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          {MONTHS.map((m, i) => (
            <div key={m}>
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{m}</Label>
              <Input type="number" value={rates[i]} onChange={(e) => setRates((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))} className="mt-1 h-9 tabular-nums" data-testid={`rate-input-${i}`} />
            </div>
          ))}
        </div>
        <div className="rounded-lg bg-[hsl(var(--success))]/8 border border-[hsl(var(--success))]/20 p-4 space-y-1.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--success))]">Riepilogo Cash Flow (semplificato)</div>
          <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">EBITDA</span><span className="tabular-nums font-medium">{formatCurrency(kpi.ebitda)}</span></div>
          <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">− Totale rate annuo</span><span className="tabular-nums font-medium delta-bad">− {formatCurrency(totale)}</span></div>
          <div className="border-t border-[hsl(var(--success))]/20 pt-1.5 flex items-center justify-between"><span className="text-sm font-semibold">Cash Flow</span><span className={`text-base font-semibold tabular-nums ${cashFlow >= 0 ? 'delta-good' : 'delta-bad'}`} data-testid="cashflow-result">{formatCurrency(cashFlow)}</span></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="cashflow-cancel">Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="cashflow-save">{saving ? 'Salvo…' : 'Salva rate mensili'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PfnDialog({ open, onOpenChange, kpi, assumptions, sid, onSaved }) {
  const initial = () => ({
    pfn_fine_anno_precedente: String(Math.round(kpi.pfn_fine_anno_precedente || 0)),
    crediti_fiscali_recuperati: String(Math.round(kpi.crediti_fiscali_recuperati || 0)),
    magazzino_fine_anno_precedente: String(Math.round(kpi.magazzino_fine_anno_precedente || 0)),
    magazzino_fine_anno_in_corso: String(Math.round(kpi.magazzino_fine_anno_in_corso || 0)),
    investimenti_con_cassa: String(Math.round(kpi.investimenti_con_cassa || 0)),
  });
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(initial()); /* eslint-disable-next-line */ }, [open]);

  const n = (k) => parseFloat(String(form[k]).replace(',', '.')) || 0;
  const utileNetto = kpi.utile_netto || 0;
  const deltaMag = n('magazzino_fine_anno_in_corso') - n('magazzino_fine_anno_precedente');
  const pfn = n('pfn_fine_anno_precedente') - utileNetto - n('crediti_fiscali_recuperati') + deltaMag + n('investimenti_con_cassa');

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const fieldDefs = [
    { key: 'pfn_fine_anno_precedente', label: 'PFN fine anno precedente (€)' },
    { key: 'crediti_fiscali_recuperati', label: 'Crediti fiscali recuperati (€)' },
    { key: 'magazzino_fine_anno_precedente', label: 'Magazzino fine anno precedente (€)' },
    { key: 'magazzino_fine_anno_in_corso', label: 'Magazzino fine anno in corso (€)', assumption: true },
    { key: 'investimenti_con_cassa', label: 'Investimenti con cassa (€)' },
  ];

  const save = async () => {
    setSaving(true);
    try {
      const patch = { ...(assumptions || {}) };
      fieldDefs.forEach((f) => { patch[f.key] = n(f.key); });
      await api.updateScenario(sid, { assumptions: patch });
      toast.success('Input PFN aggiornati', { description: `PFN ${formatCurrency(pfn)}` });
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error('Errore nel salvataggio degli input PFN'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pfn-dialog">
        <DialogHeader>
          <DialogTitle>Modifica input PFN</DialogTitle>
          <DialogDescription>Formula: PFN prec − Utile netto − Crediti fisc. + ΔMagazzino + Investimenti</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {fieldDefs.map((f) => (
            <div key={f.key}>
              <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                {f.label}
                {f.assumption && <span className="font-semibold text-[hsl(var(--danger))]">assunzione</span>}
              </Label>
              <Input type="number" value={form[f.key]} onChange={(e) => set(f.key, e.target.value)} className={`mt-1 h-9 tabular-nums ${f.assumption ? 'text-[hsl(var(--danger))] font-medium border-[hsl(var(--danger))]/40 focus-visible:ring-[hsl(var(--danger))]/30' : ''}`} data-testid={`pfn-input-${f.key}`} />
            </div>
          ))}
        </div>
        <div className="rounded-lg bg-muted/40 border p-4 space-y-1.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Calcolo PFN</div>
          <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">− Utile netto (calcolato)</span><span className="tabular-nums font-medium">{formatCurrency(utileNetto)}</span></div>
          <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Δ Magazzino</span><span className="tabular-nums font-medium">{formatCurrency(deltaMag)}</span></div>
          <div className="border-t pt-1.5 flex items-center justify-between"><span className="text-sm font-semibold">PFN risultante</span><span className={`text-base font-semibold tabular-nums ${pfn <= 0 ? 'delta-good' : 'delta-bad'}`} data-testid="pfn-result">{formatCurrency(pfn)}</span></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="pfn-cancel">Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="pfn-save">{saving ? 'Salvo…' : 'Salva input PFN'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function Dashboard() {
  const { active, sid, loading: scLoading, error: scError, reload } = useScenario();
  const { isAdmin } = useAuth();
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadKpi = useCallback(() => {
    if (!sid) return;
    setLoading(true); setError(false);
    api.getKpi(sid).then(setKpi).catch(() => setError(true)).finally(() => setLoading(false));
  }, [sid]);

  useEffect(() => { loadKpi(); }, [loadKpi]);
  const reloadAll = useCallback(() => { reload(); loadKpi(); }, [reload, loadKpi]);
  const [dialog, setDialog] = useState(null); // 'cashflow' | 'pfn' | null
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const downloadReport = useCallback(async () => {
    if (!sid) return;
    setDownloadingPdf(true);
    try {
      const blob = await api.getScenarioReportPdf(sid);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Report_${(active?.name || 'piano').replace(/[^a-zA-Z0-9-_]/g, '_')}_${active?.year || ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report PDF generato');
    } catch (e) {
      toast.error('Errore nella generazione del report');
    } finally {
      setDownloadingPdf(false);
    }
  }, [sid, active]);

  if (scLoading || (loading && !kpi)) return (<div><PageHeader title="Dashboard" description="Panoramica del piano industriale attivo" /><LoadingKpis /></div>);
  if (scError || error || !kpi) return (<div><PageHeader title="Dashboard" /><ErrorState message="Impossibile caricare i KPI del piano" onRetry={reload} /></div>);

  // ---- Semafori qualità dei risultati (indicatori principali) ----
  // EBITDA: verde >= 10%, giallo 8–10%, rosso < 8%
  const ebitdaStatus = kpi.ebitda_pct >= 0.10 ? 'good' : kpi.ebitda_pct >= 0.08 ? 'warn' : 'bad';
  // Cash Flow: verde se > 0, altrimenti rosso
  const cashStatus = kpi.cash_flow > 0 ? 'good' : 'bad';
  // Leva finanziaria PFN/EBITDA: verde < 3, giallo 3–4, rosso > 4 (PFN negativa = cassa netta → verde)
  const leverage = kpi.ebitda > 0 ? kpi.pfn / kpi.ebitda : null;
  let leverageStatus, leverageValue;
  if (kpi.pfn <= 0) { leverageStatus = 'good'; }
  else if (leverage === null) { leverageStatus = 'bad'; }
  else if (leverage < 3) { leverageStatus = 'good'; }
  else if (leverage <= 4) { leverageStatus = 'warn'; }
  else { leverageStatus = 'bad'; }
  leverageValue = kpi.ebitda > 0 ? formatNumber(leverage, 2) + 'x' : 'n/d';
  // DSCR: verde >= 1.2, giallo 1.0–1.2, rosso < 1.0 (soglie bancarie standard)
  const dscrStatus = kpi.dscr >= 1.2 ? 'good' : kpi.dscr >= 1.0 ? 'warn' : 'bad';

  const cards = [
    { label: 'Ricavi totali', value: formatCurrency(kpi.revenues_total), sub: `Mrg op. ${formatPercent(kpi.margine_operativo_pct)} · ${formatEurPerDay(kpi.daily_sales_total)}`, icon: Euro },
    { label: "Utile d'esercizio", value: formatCurrency(kpi.utile_esercizio), sub: `Redditività ${formatPercent(kpi.redditivita)}`, tone: kpi.utile_esercizio >= 0 ? 'good' : 'bad', delta: formatPercent(kpi.redditivita), icon: TrendingUp },
    { label: 'EBITDA', value: formatCurrency(kpi.ebitda), sub: `${formatPercent(kpi.ebitda_pct)} sui ricavi`, tone: ebitdaStatus, status: ebitdaStatus, delta: formatPercent(kpi.ebitda_pct), icon: Activity },
    { label: 'Cash Flow', value: formatCurrency(kpi.cash_flow), sub: `${formatPercent(kpi.cash_flow_pct)} sui ricavi`, tone: cashStatus, status: cashStatus, icon: Wallet, onClick: isAdmin ? () => setDialog('cashflow') : undefined, actionHint: isAdmin ? 'Tocca per modificare le rate' : undefined },
    { label: 'PFN', value: formatCurrency(kpi.pfn), sub: kpi.pfn < 0 ? 'Posizione di cassa netta' : 'Debito netto', tone: kpi.pfn <= 0 ? 'good' : 'warn', icon: Banknote, onClick: isAdmin ? () => setDialog('pfn') : undefined, actionHint: isAdmin ? 'Tocca per modificare gli input' : undefined },
    { label: 'PFN / EBITDA', value: leverageValue, sub: 'Leva finanziaria (verde <3 · giallo 3–4)', status: leverageStatus, icon: Scale },
    { label: 'DSCR', value: formatNumber(kpi.dscr, 2) + 'x', sub: 'EBITDA / Rate mutui (verde ≥1.2 · giallo 1.0–1.2)', status: dscrStatus, icon: Gauge },
    { label: 'Valutazione', value: formatCurrency(kpi.valutazione), sub: `EBITDA × ${formatNumber(active?.assumptions?.ebitda_multiplier || 6, 0)} − PFN`, icon: Building2 },
    { label: 'BEP fatturato', value: formatCurrency(kpi.bep_fatturato), sub: `${formatNumber(kpi.bep_mese, 1)} mesi · ${formatNumber(kpi.bep_giorno_lavorativo, 0)} gg lav.`, icon: Target },
  ];

  const revByDiv = Object.entries(kpi.revenues_by_division || {}).map(([k, v]) => ({ name: DIV_LABELS[k] || k, value: v })).filter((e) => e.value > 0).sort((a, b) => b.value - a.value);
  const fixedByCat = Object.entries(kpi.fixed_costs_by_category || {}).map(([k, v]) => ({ name: k, value: v })).filter((e) => e.value > 0).sort((a, b) => b.value - a.value);

  // Ripartizione dei ricavi (come si compone ogni euro): costi variabili + costi fissi + risultato operativo (EBIT)
  const variableCosts = Math.max((kpi.revenues_total || 0) - (kpi.margine_contribuzione || 0), 0);
  const fixedCosts = Math.max(kpi.fixed_costs_annual || 0, 0);
  const ebitdaResid = Math.max((kpi.revenues_total || 0) - variableCosts - fixedCosts, 0);
  const ripartizione = [
    { name: 'Costi variabili', value: variableCosts },
    { name: 'Costi fissi', value: fixedCosts },
    { name: 'Risultato operativo', value: ebitdaResid },
  ].filter((e) => e.value > 0);

  // Contributo produzione EU/Asia — mix volumi per la torta
  const prodMix = [
    { name: 'Asia', value: (kpi.prod_volume_cina_wp || 0) / 1000 },
    { name: 'EU/Italia', value: (kpi.prod_volume_ita_wp || 0) / 1000 },
  ].filter((e) => e.value > 0);

  return (
    <div className="space-y-6">
      <PageHeader title="Dashboard" description={`Piano attivo: ${active?.name} · ${active?.year}`} actions={(
        <Button onClick={downloadReport} disabled={downloadingPdf} data-testid="download-report-pdf-btn">
          {downloadingPdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          {downloadingPdf ? 'Generazione…' : 'Report PDF'}
        </Button>
      )} />

      <div className="grid grid-cols-12 gap-4">
        {cards.map((c, i) => (
          <div key={i} className="col-span-12 sm:col-span-6 xl:col-span-3"><KpiCard {...c} /></div>
        ))}
      </div>

      {/* Conto Economico + Grafici a torta principali */}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 xl:col-span-5">
          <ContoEconomico kpi={kpi} assumptions={active?.assumptions} canEdit={isAdmin} sid={sid} onSaved={reloadAll} />
        </div>
        <div className="col-span-12 md:col-span-6 xl:col-span-4">
          <ChartCard title="Ripartizione dei ricavi" subtitle="Costi variabili, costi fissi e risultato operativo (annuo)">
            <DonutChart data={ripartizione} testId="pie-ripartizione" />
          </ChartCard>
        </div>
        <div className="col-span-12 md:col-span-6 xl:col-span-3">
          <ChartCard title="Ricavi per divisione" subtitle="Ripartizione del fatturato">
            <DonutChart data={revByDiv} testId="pie-ricavi-divisione" />
          </ChartCard>
        </div>
        <div className="col-span-12">
          <ChartCard title="Costi fissi per categoria" subtitle="Composizione annua dei costi fissi">
            <DonutChart data={fixedByCat} testId="pie-costi-fissi" />
          </ChartCard>
        </div>
      </div>

      {/* Margine medio per divisione */}
      <div>
        <div className="mb-3 flex items-center gap-2"><Percent className="h-4 w-4 text-muted-foreground" /><h3 className="text-sm font-semibold text-foreground">Margine medio per divisione</h3></div>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 lg:col-span-6">
            <DivisionMarginCard title="Installatori" icon={Boxes}
              revenue={kpi.product_mix_revenue} dailySales={kpi.daily_sales_installatori} marginBase={kpi.product_mix_weighted_margin} coverage={kpi.financial_coverage_installatori_pct}
              marginEff={kpi.product_mix_weighted_margin_effective} marginEur={kpi.product_mix_margin_eur_effective} />
          </div>
          <div className="col-span-12 lg:col-span-6">
            <DivisionMarginCard title="Impianti" icon={Building2}
              revenue={kpi.client_mix_revenue} dailySales={kpi.daily_sales_impianti} marginBase={kpi.client_mix_weighted_margin} coverage={kpi.financial_coverage_impianti_pct}
              marginEff={kpi.client_mix_weighted_margin_effective} marginEur={kpi.client_mix_margin_eur_effective} />
          </div>
        </div>
      </div>

      {/* Contributo del mix produzione EU/Asia */}
      <div>
        <div className="mb-3 flex items-center gap-2"><Globe className="h-4 w-4 text-muted-foreground" /><h3 className="text-sm font-semibold text-foreground">Contributo del mix produzione EU/Asia</h3></div>
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-5">
            <ChartCard title="Volume per origine" subtitle={`Mix: Asia ${formatPercent(kpi.prod_mix_cina)} · EU/Italia ${formatPercent(kpi.prod_mix_ita)}`}>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={prodMix} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={82} paddingAngle={2}>
                    <Cell fill="hsl(38,92%,50%)" /><Cell fill="hsl(215,14%,52%)" />
                  </Pie>
                  <Tooltip formatter={(v) => formatNumber(v, 0) + ' kWp'} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 flex flex-wrap justify-center gap-x-6 gap-y-1.5 text-xs">
                {prodMix.map((e, i) => (
                  <span key={i} className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: i === 0 ? 'hsl(38,92%,50%)' : 'hsl(215,14%,52%)' }} />
                    <span className="text-muted-foreground">{e.name}</span>
                    <span className="font-semibold tabular-nums text-foreground">{formatNumber(e.value, 0)} kWp</span>
                  </span>
                ))}
              </div>
            </ChartCard>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <Card className="p-5 h-full" data-testid="prod-contribution-card">
              <div className="flex items-center gap-2 mb-3"><span className="h-8 w-8 rounded-lg bg-primary/10 text-primary grid place-items-center"><Factory className="h-4 w-4" /></span><h4 className="text-sm font-semibold text-foreground">Vantaggio di costo (Gap EU/Asia)</h4></div>
              <div className="grid grid-cols-2 gap-x-8">
                <div>
                  <Stat label="Gap EU/Asia (beneficio)" value={formatCurrency(kpi.gap_eu_asia)} strong testId="stat-gap" />
                  <Stat label="Costo €/Wp Asia" value={formatEurPerWp(kpi.prod_cost_wp_cina)} />
                  <Stat label="Costo €/Wp EU/Italia" value={formatEurPerWp(kpi.prod_cost_wp_ita)} />
                </div>
                <div>
                  <Stat label="Costo medio ponderato" value={formatEurPerWp(kpi.prod_cost_weighted_wp)} strong />
                  <Stat label="Volume totale" value={formatNumber((kpi.prod_volume_wp || 0) / 1000, 0)} hint="kWp" />
                  <Stat label="Quota prodotta in Asia" value={formatPercent(kpi.prod_mix_cina)} />
                </div>
              </div>
              <p className="mt-3 text-[11px] text-muted-foreground">Il Gap rappresenta il minor costo industriale ottenuto producendo in Asia rispetto alla produzione EU/Italia, a parità di volumi.</p>
            </Card>
          </div>
        </div>
      </div>

      {/* Modali editing Cash Flow / PFN */}
      <CashFlowDialog open={dialog === 'cashflow'} onOpenChange={(o) => setDialog(o ? 'cashflow' : null)} kpi={kpi} assumptions={active?.assumptions} sid={sid} onSaved={reloadAll} />
      <PfnDialog open={dialog === 'pfn'} onOpenChange={(o) => setDialog(o ? 'pfn' : null)} kpi={kpi} assumptions={active?.assumptions} sid={sid} onSaved={reloadAll} />
    </div>
  );
}
